import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { getGroupDetails } from '../../../services/groupService';

const GroupMembersModal = ({ isOpen, onClose, groupId }) => {
  const [groupDetails, setGroupDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen && groupId) {
      loadGroupDetails();
    }
  }, [isOpen, groupId]);

  const loadGroupDetails = async () => {
    setIsLoading(true);
    try {
      const details = await getGroupDetails(groupId);
      setGroupDetails(details);
    } catch (error) {
      console.error('Error loading group details:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'owner':
        return 'Crown';
      case 'editor':
        return 'Edit';
      case 'viewer':
        return 'Eye';
      default:
        return 'User';
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'owner':
        return 'bg-yellow-500/20 text-yellow-700 border-yellow-500/30';
      case 'editor':
        return 'bg-blue-500/20 text-blue-700 border-blue-500/30';
      case 'viewer':
        return 'bg-gray-500/20 text-gray-700 border-gray-500/30';
      default:
        return 'bg-gray-500/20 text-gray-700 border-gray-500/30';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card rounded-xl border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-foreground">Group Members</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {groupDetails?.name || 'Loading...'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
            aria-label="Close"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-6">
          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
              <p className="text-muted-foreground mt-4">Loading members...</p>
            </div>
          ) : groupDetails?.members?.length === 0 ? (
            <div className="text-center py-12">
              <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No members yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {groupDetails?.members?.map((member) => (
                <div
                  key={member?.id}
                  className="flex items-center justify-between p-4 bg-muted/50 rounded-lg border border-border hover:bg-muted transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                      <Icon name="User" size={20} className="text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">
                        User {member?.userId?.slice(0, 8)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Joined {new Date(member?.joinedAt)?.toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium border ${
                        getRoleBadgeColor(member?.role)
                      }`}
                    >
                      <Icon name={getRoleIcon(member?.role)} size={12} className="inline mr-1" />
                      {member?.role}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Group Info */}
          {groupDetails && (
            <div className="mt-6 pt-6 border-t border-border">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-accent/10 rounded-lg">
                  <Icon name="Users" size={24} className="text-accent mx-auto mb-2" />
                  <p className="text-2xl font-bold text-foreground">{groupDetails?.members?.length}</p>
                  <p className="text-xs text-muted-foreground">Total Members</p>
                </div>
                <div className="text-center p-4 bg-primary/10 rounded-lg">
                  <Icon name="Calendar" size={24} className="text-primary mx-auto mb-2" />
                  <p className="text-sm font-medium text-foreground">
                    {new Date(groupDetails?.createdAt)?.toLocaleDateString()}
                  </p>
                  <p className="text-xs text-muted-foreground">Created</p>
                </div>
              </div>
            </div>
          )}

          <div className="mt-6">
            <Button
              variant="outline"
              size="lg"
              fullWidth
              onClick={onClose}
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupMembersModal;